/* storybook-check-ignore */
import dynamic from 'next/dynamic';
import { Listing, ListingWithComputedProperties } from 'declarations/exclusives/listing';
import { Flex, Text, BoxProps, Box } from '@opendoor/bricks/core';
import { useMemo, useRef } from 'react';
import AppraisalPriceMatchGuaranteeInfo from '../AppraisalPriceMatchGuaranteeInfo';
import { css } from '@emotion/react';
import AccountConnector, { AccountProps } from 'components/shared/AccountConnector';
import {
  getHasOngoingOrUpcomingOpenHouses,
  getIsOOMLS,
  getIsSellerOfferExperiment,
  getListingPriceCents,
  getOriginalListingPriceCents,
} from 'helpers/exclusives/listing';
import { ListingStates } from '../constants/constants';
import { useInView, motion, AnimatePresence } from 'framer-motion';
import { WatchlistButtonWithoutContextProvider } from '../WatchlistButton';
import { getEarliestOpenHouse } from 'helpers/exclusives/exclusiveListingInfo';
import OpenHouseBanner from '../openHouse/OpenHouseBanner';
import SelfTourButton from './SelfTourButton';
import BuyButton from './BuyButton';
import ImInterestedButton from './ImInterestedButton';
import NoPriceInfo from './NoPriceInfo';
import WithPriceAdditionalStateInfo from './WithPriceAdditionalStateInfo';
import WhyDoIPayLess from './WhyDoIPayLess';
import Benefits from '../Benefits';
import RequestATourButton, { shouldShowRequestATourButton } from './RequestATourButton';
import { media } from '@opendoor/bricks/theme/legacy/foundations/mediaBreakpoints';
import LetterIcon from '../icons/LetterIcon';
import OOMLSBenefits from '../OOMLSBenefits';
import TourRequested from './RequestATour/TourRequested';
import RequestATour, { useRequestATourContext } from './RequestATour';

const ExclusivePeriodEndsIn = dynamic(() => import('./ExclusivePeriodEndsIn'), {
  ssr: false,
});

const MotionFlex = motion(Flex);

interface ButtonState {
  text?: string;
  disabled?: boolean;
  visible?: boolean;
  primary?: boolean;
}

function getIsActiveReservationCustomer(listing: Listing, account: AccountProps) {
  return (
    !account.infoIsLoading &&
    account.human &&
    account.human.get('customer_uuid') &&
    listing.exclusive_listing_info.reservation_customer_uuid === account.human.get('customer_uuid')
  );
}

const buyButtonByState: Record<
  ListingStates,
  (
    it: Listing,
    acc: AccountProps,
  ) => Omit<ButtonState, 'primary'> & { type: 'make_offer' | 'reserve' }
> = {
  [ListingStates.EL_SETUP]: () => ({
    visible: false,
    type: 'reserve',
  }),
  [ListingStates.RESERVATION_PENDING]: (listing, acc) => {
    const isSellerExperiment = getIsSellerOfferExperiment(listing);

    return {
      visible: getIsActiveReservationCustomer(listing, acc),
      type: isSellerExperiment ? 'make_offer' : 'reserve',
      text: isSellerExperiment
        ? 'Make an Offer'
        : `Buy it now for $${(getListingPriceCents(listing) / 100).toLocaleString()}`,
    };
  },
  [ListingStates.EL_LISTED]: (listing) => {
    const isSellerExperiment = getIsSellerOfferExperiment(listing);

    return {
      visible: true,
      type: isSellerExperiment ? 'make_offer' : 'reserve',
      text: isSellerExperiment
        ? 'Make an Offer'
        : `Buy it now for $${(getListingPriceCents(listing) / 100).toLocaleString()}`,
    };
  },
  [ListingStates.OOMLS]: (listing) => {
    return {
      visible: true,
      type: 'reserve',
      text: `Buy it for $${(getListingPriceCents(listing) / 100).toLocaleString()}`,
    };
  },
  [ListingStates.EL_IN_CONTRACT]: () => ({
    text: 'Unavailable to buy',
    disabled: true,
    visible: false,
    type: 'reserve',
  }),
  [ListingStates.EL_RESERVATION_LOCKED]: () => ({
    text: 'Unavailable to buy',
    disabled: true,
    visible: false,
    type: 'reserve',
  }),
  [ListingStates.EL_EXPIRED]: () => ({
    text: 'Unavailable to buy',
    disabled: true,
    visible: true,
    type: 'reserve',
  }),
  [ListingStates.EL_SOLD]: (listing) => {
    const isSellerExperiment = getIsSellerOfferExperiment(listing);

    return {
      visible: true,
      disabled: true,
      type: isSellerExperiment ? 'make_offer' : 'reserve',
      text: isSellerExperiment
        ? 'Make an Offer'
        : `Buy it now for for $${(getListingPriceCents(listing) / 100).toLocaleString()}`,
    };
  },
};

const selfTourButtonByState: Record<
  ListingStates,
  (it: Listing, acc: AccountProps) => ButtonState
> = {
  [ListingStates.EL_SETUP]: () => ({
    visible: false,
    text: 'Self tour instantly',
  }),
  [ListingStates.RESERVATION_PENDING]: (listing) => ({
    text: 'Self tour instantly',
    visible: !getIsSellerOfferExperiment(listing),
    disabled: false,
  }),
  [ListingStates.EL_LISTED]: (listing) => ({
    text: 'Self tour instantly',
    visible: !getIsSellerOfferExperiment(listing),
  }),
  [ListingStates.OOMLS]: (listing) => ({
    text: 'Self tour instantly',
    visible: !getIsSellerOfferExperiment(listing),
  }),
  [ListingStates.EL_IN_CONTRACT]: (listing) => ({
    text: 'Self tour instantly',
    disabled: false,
    visible: !getIsSellerOfferExperiment(listing),
  }),
  [ListingStates.EL_RESERVATION_LOCKED]: (listing) => ({
    text: 'Self tour instantly',
    disabled: true,
    visible: !getIsSellerOfferExperiment(listing),
  }),
  [ListingStates.EL_EXPIRED]: () => ({
    text: 'Self tour unavailable',
    disabled: true,
  }),
  [ListingStates.EL_SOLD]: (listing) => ({
    text: 'Self tour instantly',
    disabled: true,
    visible: !getIsSellerOfferExperiment(listing),
  }),
};

const watchlistButtonByState: (
  listing: ListingWithComputedProperties,
  account: AccountProps,
) => Record<ListingStates, ButtonState> = (listing, account) => ({
  [ListingStates.EL_SETUP]: {
    visible: !getIsSellerOfferExperiment(listing) || !getHasOngoingOrUpcomingOpenHouses(listing),
    primary: true,
  },
  [ListingStates.RESERVATION_PENDING]: {
    visible: !getIsActiveReservationCustomer(listing, account),
    primary: false,
  },
  [ListingStates.EL_LISTED]: {
    visible: false,
    primary: true,
  },
  [ListingStates.OOMLS]: {
    visible: false,
    primary: true,
  },
  [ListingStates.EL_IN_CONTRACT]: {
    visible: true,
    primary: getIsSellerOfferExperiment(listing),
  },
  [ListingStates.EL_RESERVATION_LOCKED]: {
    visible: true,
    primary: false,
  },
  [ListingStates.EL_EXPIRED]: {
    visible: false,
    primary: false,
  },
  [ListingStates.EL_SOLD]: {
    visible: false,
    primary: false,
  },
});

function priceTitle(isOOMLS: boolean, hasPrice: boolean, isSellerExperiment: boolean) {
  if (isOOMLS) {
    return 'Buy it directly on Exclusives ';
  }
  if (!hasPrice) {
    return 'Exclusive Price';
  }
  if (isSellerExperiment) {
    return 'Your Exclusive Price';
  }
  return 'Current Exclusive Price';
}

function HeroPriceAndBenefitsSection({
  listing,
  onTourButtonClick,
  boxProps,
  additionalConditionToShowBottomBar = true,
  ...account
}: {
  listing: ListingWithComputedProperties;
  onTourButtonClick: () => void;
  boxProps?: BoxProps;
  additionalConditionToShowBottomBar?: boolean;
} & AccountProps) {
  const requestATour = useRequestATourContext();
  const earliestOpenHouse = useMemo(
    () => getEarliestOpenHouse(listing.exclusive_listing_info),
    [listing],
  );
  const priceSectionRef = useRef<HTMLDivElement>(null);
  const initialSectionRef = useRef<HTMLDivElement>(null);
  const isPriceSectionInView = useInView(priceSectionRef);
  const isInitialSectionInView = useInView(initialSectionRef);
  const showBottomBar =
    !isPriceSectionInView && !isInitialSectionInView && additionalConditionToShowBottomBar;
  const isSellerOfferExperiment = getIsSellerOfferExperiment(listing);
  const isAvailableSoon = listing.el_state === ListingStates.EL_SETUP;
  const isOOMLS = getIsOOMLS(listing);
  const originalPriceCents = getOriginalListingPriceCents(listing, isOOMLS);
  const originalPrice = originalPriceCents && originalPriceCents / 100;
  const priceCents = getListingPriceCents(listing);
  const price = priceCents && priceCents / 100;
  const hasPrice = !!priceCents && priceCents > 0;
  const elState = listing.el_state;
  const hasOngoingOrUpcomingOpenHouses = getHasOngoingOrUpcomingOpenHouses(listing);
  const buyButtonProps = buyButtonByState[elState](listing, account);
  const selfTourButtonProps = selfTourButtonByState[elState](listing, account);
  const watchListButtonState = watchlistButtonByState(listing, account)[elState];
  const showOpenHouseBanner =
    isSellerOfferExperiment && earliestOpenHouse && !requestATour.isFeatureFlagEnabled;
  const showRequestATourButton = shouldShowRequestATourButton(listing);
  const showBottomInfoAndActions =
    !isOOMLS &&
    (showOpenHouseBanner ||
      (showRequestATourButton &&
        (!requestATour.isFeatureFlagEnabled || !requestATour.isButtonHidden)) ||
      selfTourButtonProps.visible ||
      watchListButtonState.visible);
  return (
    <>
      <Flex
        ref={initialSectionRef}
        flexDirection="column"
        maxWidth={['644px', '644px', '644px', '942px']}
        width={['unset', null, 'calc(100% - 3rem)', 'unset']}
        overflowX={'hidden'}
        ml={['1.5rem', '1.5rem', 'auto', '3.5rem']}
        mr={['1.5rem', '1.5rem', 'auto', '2rem']}
        gridGap="1.25rem"
      >
        {hasPrice && <WithPriceAdditionalStateInfo listing={listing} />}
        {!hasPrice && !isSellerOfferExperiment && <NoPriceInfo listing={listing} />}
        {listing.exclusive_listing_info.end_time && !isOOMLS && (
          <ExclusivePeriodEndsIn endTime={listing.exclusive_listing_info.end_time} />
        )}
        <TourRequested listing={listing} />
      </Flex>
      <Flex
        flexDirection="column"
        bg="#FAFBFF"
        borderBottom="2.42424px solid rgba(207, 215, 251, 0.41)"
        width="100%"
        mt="1.25rem"
        pb="1.25rem"
        flex={1}
        position="relative"
      >
        <Box
          zIndex="0"
          borderBottom="2.42424px solid rgba(207, 215, 251, 0.41)"
          position="absolute"
          bg="neutrals0"
          height={['52px', '52px', '52px', '70px']}
          width="100%"
        />
        <Flex
          ref={priceSectionRef}
          zIndex={1}
          flexDirection="column"
          maxWidth={['644px', '644px', '644px', '942px']}
          flexGrow={1}
          flexShrink={0}
          width={['unset', null, 'calc(100% - 3rem)', 'unset']}
          ml={['1.5rem', '1.5rem', 'auto', '3.5rem']}
          mr={['1.5rem', '1.5rem', 'auto', '2rem']}
        >
          <Flex
            flexDirection="column"
            width="100%"
            gridGap="1.25rem"
            mb="1.25rem"
            {...(watchListButtonState.visible
              ? { pb: ['unset', 'unset', 'unset', '0.5rem'], pt: '1rem' }
              : undefined)}
            {...boxProps}
          >
            <Flex
              bg="neutrals0"
              padding="1.5rem 0 1.875rem 0"
              border="1px solid #CFD7FB"
              boxShadow="0px 2px 7px rgba(150, 162, 224, 0.13), 0px 4px 8px rgba(138, 145, 180, 0.06)"
              borderRadius="semiRounded"
              flexDirection="column"
              gridGap="2rem"
            >
              <Flex
                flexDirection="row"
                paddingX="1.875rem"
                alignItems="self-end"
                flexWrap="wrap"
                gridGap="var(--gridGap)"
                paddingBottom="2rem"
                borderBottom="1px solid #CFD7FB"
                padding-left={['1.875rem', '1.875rem', 'unset', 'unset']}
                padding-right={['1.875rem', '1.875rem', 'unset', 'unset']}
                margin-left={['unset', 'unset', '1.875rem', '1.875rem']}
                margin-right={['unset', 'unset', '1.875rem', '1.875rem']}
                css={css`
                  --gridGap: 1rem;
                  ${media.largerThan.MD} {
                    --gridGap: 2rem;
                  }
                `}
                {...(!showBottomInfoAndActions && {
                  borderBottom: 'unset',
                  paddingBottom: 'unset',
                })}
              >
                <Flex
                  flexDirection="column"
                  gridGap="0.4rem"
                  minWidth="calc(50% - 0.5px - var(--gridGap))"
                >
                  <Text
                    fontSize={['s1', null, null, null, 's0', 's1']}
                    style={{ fontWeight: 500 }}
                    opacity="0.9"
                    color="brand50"
                  >
                    {priceTitle(isOOMLS, hasPrice, isSellerOfferExperiment)}
                  </Text>
                  <Text fontSize="s4" lineHeight="s3" color="brand50" style={{ fontWeight: 900 }}>
                    {!price ? 'Available Soon' : `$${price.toLocaleString()}`}
                  </Text>
                  <Box>
                    {!isOOMLS ? (
                      <AppraisalPriceMatchGuaranteeInfo
                        isSellerOfferExperiment={isSellerOfferExperiment}
                        buttonProps={{
                          fontSize: ['s0', null, null, null, 's00', 's0'],
                          lineHeight: 's00',
                          flexShrink: 1,
                          flexGrow: 0,
                        }}
                        iconSize={16}
                      />
                    ) : (
                      <Text
                        fontSize={['s0', null, null, null, 's00', 's0']}
                        opacity="0.7"
                        style={{ color: '#001892' }}
                      >
                        Listed at ${originalPrice?.toLocaleString()} everywhere else
                      </Text>
                    )}
                  </Box>
                </Flex>
                {!!originalPrice && (
                  <>
                    <Flex
                      bg="#CFD7FB"
                      height={['1px', '1px', '5rem', '5rem']}
                      width={['100%', '100%', '1px', '1px']}
                    />
                    {!isOOMLS && (
                      <Flex flexDirection="column" gridGap="0.4rem">
                        <Text
                          fontSize={['s1', null, null, null, 's0', 's1']}
                          color="neutrals90"
                          opacity="0.9"
                        >
                          {isSellerOfferExperiment
                            ? 'Seller’s Asking Price'
                            : 'Future public list price'}
                        </Text>
                        <Text
                          fontSize="s4"
                          lineHeight="s3"
                          textDecoration="line-through"
                          opacity="0.8"
                          style={{ color: '#333333' }}
                        >
                          ${originalPrice.toLocaleString()}
                        </Text>
                        <Box>
                          <WhyDoIPayLess
                            isSellerOfferExperiment={isSellerOfferExperiment}
                            fontSize="s0"
                            lineHeight="s00"
                            flexShrink={1}
                            flexGrow={0}
                          />
                        </Box>
                      </Flex>
                    )}
                    {isOOMLS && (
                      <Flex flexDirection="column" gridGap="0.5rem">
                        <Text
                          as="div"
                          display="flex"
                          gridGap="0.75rem"
                          alignItems="center"
                          lineHeight="s00"
                          fontSize={['s1', null, null, null, 's0', 's1']}
                          fontWeight="semibold"
                          color="brand50"
                        >
                          <Flex
                            width="2rem"
                            height="2rem"
                            bg="#EBEFFD"
                            borderRadius="roundedSquare"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <LetterIcon size={18} />
                          </Flex>
                          Appraisal Price
                          <br />
                          Match Guarantee
                        </Text>
                        <Text
                          fontSize={['s0', null, null, null, 's00', 's0']}
                          opacity="0.7"
                          style={{ color: '#001892' }}
                        >
                          If the home appraises for less,
                          <br />
                          we’ll match it up to $50k.
                        </Text>
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
              {showBottomInfoAndActions && (
                <Flex flexDirection="column" gridGap="0.5rem" paddingX="1.875rem">
                  {isSellerOfferExperiment &&
                    earliestOpenHouse &&
                    !requestATour.isFeatureFlagEnabled && (
                      <OpenHouseBanner listing={listing} paddingTop="0" />
                    )}
                  {selfTourButtonProps.visible && (
                    <SelfTourButton
                      text={selfTourButtonProps.text}
                      onClick={onTourButtonClick}
                      disabled={selfTourButtonProps?.disabled}
                    />
                  )}
                  {watchListButtonState.visible && (
                    <Flex>
                      <WatchlistButtonWithoutContextProvider
                        account={account}
                        isPrimary={watchListButtonState.primary}
                        listing={listing}
                      />
                    </Flex>
                  )}
                  {shouldShowRequestATourButton(listing) && requestATour.isFeatureFlagEnabled ? (
                    <RequestATour />
                  ) : (
                    <Flex flexDirection="column" gridGap="1.5rem">
                      {!hasOngoingOrUpcomingOpenHouses && (
                        <Text
                          fontSize="s2"
                          lineHeight="s0"
                          fontWeight="semibold"
                          color="neutrals100"
                        >
                          Tour this home
                          <br />
                          <Text as="span" fontSize="s1" fontWeight="normal" opacity="0.8">
                            This home is open to tours upon request. Let us know your preferred
                            dates and we’ll coordinate with the seller to arrange a showing.
                          </Text>
                        </Text>
                      )}
                      <RequestATourButton listing={listing} />
                    </Flex>
                  )}
                </Flex>
              )}
            </Flex>
          </Flex>
          {selfTourButtonProps.visible && isOOMLS && (
            <SelfTourButton
              text={selfTourButtonProps.text}
              onClick={onTourButtonClick}
              disabled={selfTourButtonProps?.disabled}
              marginBottom="0.75rem"
            />
          )}
          {buyButtonProps.visible && (!isSellerOfferExperiment || priceCents === 0) && (
            <BuyButton
              listing={listing}
              disabled={buyButtonProps.disabled}
              text={buyButtonProps.text}
              type={buyButtonProps.type}
            />
          )}
          {isSellerOfferExperiment && priceCents > 0 && (
            <ImInterestedButton isPrimary={false} listing={listing} />
          )}
          {isOOMLS && (
            <Text
              fontSize="s0"
              lineHeight="s00"
              marginTop="0.75rem"
              textAlign="center"
              opacity="0.8"
              style={{ color: '#333333' }}
            >
              If you buy this home on Exclusives, you can back out at any time. No penalties.
            </Text>
          )}
        </Flex>
        {!isOOMLS && (
          <Flex
            zIndex={1}
            flexDirection="column"
            maxWidth={['644px', '644px', '644px', '942px']}
            flexGrow={1}
            flexShrink={0}
            width={['unset', 'unset', 'calc(100% - 3rem)', 'unset']}
            ml={['1.5rem', '1.5rem', 'auto', '3.5rem']}
            mr={['1.5rem', '1.5rem', 'auto', '2rem']}
          >
            <Benefits listing={listing} />
          </Flex>
        )}
      </Flex>
      {isOOMLS && <OOMLSBenefits listing={listing} />}

      <AnimatePresence exitBeforeEnter>
        {showBottomBar && (
          <MotionFlex
            left="0"
            zIndex="5"
            width="100%"
            bottom="0"
            p={['1rem', '1rem', '1rem', 0]}
            display={['flex', 'flex', 'flex', 'none']}
            flexDirection="column"
            position={['fixed', 'fixed', 'fixed', 'inherit']}
            backgroundColor={['neutrals0', 'neutrals0', 'neutrals0', 'transparent']}
            boxShadow={[
              '0px -1px 10px rgba(68, 89, 109, 0.1)',
              '0px -1px 10px rgba(68, 89, 109, 0.1)',
              '0px -1px 10px rgba(68, 89, 109, 0.1)',
              'none',
            ]}
            gridGap="0.5rem"
            initial={{ y: 300 }}
            animate={{ y: 0 }}
            exit={{ y: 300 }}
            transition={{ type: 'tween', duration: 0.45 }}
          >
            {/* {earliestOpenHouse && <OpenHouseBanner listing={listing} />} */}
            {!isAvailableSoon && (
              <>
                {!isOOMLS && (
                  <ExclusivePeriodEndsIn endTime={listing.exclusive_listing_info.end_time} />
                )}
                <Flex flexDirection="row" gridGap="0.5rem">
                  <Text fontSize="s3" lineHeight="s2" color="brand50" fontWeight="semibold">
                    {price ? `$${price.toLocaleString()}` : 'Available Soon'}
                  </Text>
                  {originalPrice && !isOOMLS && (
                    <Text
                      fontSize="s2"
                      lineHeight="s1"
                      textDecoration="line-through"
                      opacity="0.6"
                      style={{ color: '#0A0F1A' }}
                    >
                      ${originalPrice.toLocaleString()}
                    </Text>
                  )}
                </Flex>
                {!isSellerOfferExperiment && !isOOMLS ? (
                  <Text fontSize="s0" opacity="0.6" color="neutrals100">
                    Self tour everyday from 8AM - 8PM, back out anytime, Appraisal Price Match
                    Guarantee.
                  </Text>
                ) : (
                  <Text fontSize="s0" opacity="0.7" style={{ color: '#001892' }}>
                    Listed at ${originalPrice?.toLocaleString()} everywhere else
                  </Text>
                )}
                <Flex
                  flexDirection="row"
                  gridGap="1rem"
                  justifyContent="space-between"
                  width="100%"
                  mt="0.5rem"
                >
                  {selfTourButtonProps.visible && (
                    <SelfTourButton
                      text={selfTourButtonProps.text}
                      onClick={onTourButtonClick}
                      disabled={selfTourButtonProps?.disabled}
                    />
                  )}
                  {buyButtonProps.visible && (!isSellerOfferExperiment || priceCents === 0) && (
                    <BuyButton
                      listing={listing}
                      disabled={buyButtonProps.disabled}
                      text={buyButtonProps.text}
                      type={buyButtonProps.type}
                      shrinkText
                    />
                  )}
                  {isSellerOfferExperiment && priceCents > 0 && (
                    <ImInterestedButton isPrimary={false} listing={listing} />
                  )}
                </Flex>
              </>
            )}
            {watchListButtonState.visible && (
              <WatchlistButtonWithoutContextProvider
                account={account}
                isPrimary={watchListButtonState.primary}
                listing={listing}
              />
            )}
            {shouldShowRequestATourButton(listing) && requestATour.isFeatureFlagEnabled ? (
              <RequestATour isSingleButton />
            ) : (
              <RequestATourButton listing={listing} />
            )}
          </MotionFlex>
        )}
      </AnimatePresence>
    </>
  );
}

export default AccountConnector(HeroPriceAndBenefitsSection);
